import { useCallback, useRef } from 'react';
import {
  OwcModalDialog,
  OwcComponentHeader,
  OwcComponentFooter,
  OwcButton,
  OwcIcon,
} from '@one/react';
import { useDeprecateFlow } from '../../../../hooks/useDeprecateFlow';
import DeprecationForm from './DeprecationForm';
import DeprecationReview from './DeprecationReview';
import styles from './DeprecateReleaseModal.module.css';

/**
 * Orchestrator for the deprecate-release flow. State and side effects live in
 * `useDeprecateFlow`; the two steps render as `DeprecationForm` and
 * `DeprecationReview`. This component only wires them together.
 */
export default function DeprecateReleaseModal({
  open,
  onClose,
  groupName,
  releaseVersion,
  userEmail,
  onDeprecated,
}) {
  const modalRef = useRef(null);

  const {
    state,
    isLoading,
    setField,
    toggleVisible,
    goToForm,
    runDryRun,
    execute,
    filteredEntities,
    emailValid,
    canRunDryRun,
    selectionDrifted,
    canExecute,
    orphanCount,
    orphanAckRequired,
  } = useDeprecateFlow({
    groupName,
    releaseVersion,
    email: userEmail,
    open,
    onDeprecated,
    onClose,
  });

  // Stable per-field setters so the memoized review/table subtree is not
  // re-rendered by new closure identities on every keystroke.
  const setActiveTab = useCallback((v) => setField('activeTab', v), [setField]);
  const setSearch = useCallback((v) => setField('search', v), [setField]);
  const setShowOrphansOnly = useCallback((v) => setField('showOrphansOnly', v), [setField]);
  const setConfirmVersion = useCallback((v) => setField('confirmVersion', v), [setField]);
  const setOrphanAck = useCallback((v) => setField('orphanAck', v), [setField]);
  const setReason = useCallback((v) => setField('reason', v), [setField]);

  const handleClose = () => {
    if (isLoading) return;
    onClose?.();
  };

  const handleDryRun = (e) => {
    e?.preventDefault?.();
    // Guard against form submit (Enter key) bypassing the disabled button.
    if (!canRunDryRun || isLoading) return;
    runDryRun(state.selectedPks);
  };

  return (
    <OwcModalDialog
      visible={open}
      size="large"
      ref={modalRef}
      rounded
      onVisibleChange={handleClose}
      backdropOpacity={0.7}
      style={{ zIndex: 6000 }}
    >
      <OwcComponentHeader slot="header" icon="trash" status="error" separator>
        Deprecate Release : {releaseVersion}
        <div slot="suffix" className={styles.headerSuffix}>
          <OwcButton
            onClick={handleClose}
            variant="tertiary"
            aria-label="Close"
            rounded="true"
            disabled={isLoading}
          >
            ✕
          </OwcButton>
        </div>
      </OwcComponentHeader>

      {state.step === 'form' && (
        <DeprecationForm
          reason={state.reason}
          onReasonChange={setReason}
          emailValid={emailValid}
          errorMessage={state.errorMessage}
          isLoading={isLoading}
          onSubmit={handleDryRun}
        />
      )}

      {state.step === 'review' && (
        <DeprecationReview
          plan={state.plan}
          releaseVersion={releaseVersion}
          selectedPks={state.selectedPks}
          filteredEntities={filteredEntities}
          orphanCount={orphanCount}
          activeTab={state.activeTab}
          search={state.search}
          showOrphansOnly={state.showOrphansOnly}
          confirmVersion={state.confirmVersion}
          orphanAck={state.orphanAck}
          orphanAckRequired={orphanAckRequired}
          selectionDrifted={selectionDrifted}
          errorMessage={state.errorMessage}
          isLoading={isLoading}
          onTabChange={setActiveTab}
          onSearchChange={setSearch}
          onOrphansToggle={setShowOrphansOnly}
          onToggleVisible={toggleVisible}
          onConfirmChange={setConfirmVersion}
          onOrphanAckChange={setOrphanAck}
        />
      )}

      <OwcComponentFooter slot="footer" separator>
        <div className={styles.footerBar}>
          <div>
            {state.step === 'review' && (
              <OwcButton variant="tertiary" size="medium" onClick={goToForm} disabled={isLoading}>
                <OwcIcon name="chevron_left" family="outlined" /> Back
              </OwcButton>
            )}
          </div>
          <div className={styles.footerActions}>
            <OwcButton variant="secondary" size="medium" onClick={handleClose} disabled={isLoading}>
              Cancel
            </OwcButton>
            {state.step === 'form' ? (
              <OwcButton
                variant="primary"
                size="medium"
                onClick={handleDryRun}
                disabled={!canRunDryRun || isLoading}
              >
                Run dry-run analysis
              </OwcButton>
            ) : selectionDrifted ? (
              <OwcButton
                variant="primary"
                size="medium"
                onClick={() => runDryRun(state.selectedPks)}
                disabled={isLoading}
              >
                Re-run analysis
              </OwcButton>
            ) : (
              <OwcButton
                variant="primary"
                size="medium"
                onClick={execute}
                disabled={!canExecute}
                style={{ '--one-button-background-color': 'var(--one-color-cobas-red-500, #c00)' }}
              >
                Execute deprecation
              </OwcButton>
            )}
          </div>
        </div>
      </OwcComponentFooter>
    </OwcModalDialog>
  );
}
